import { Box, Button, IconButton, Stack, TextField, Typography, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { UomPicker } from "@/components/UomPicker";
import type { ItemUomInput, UomDto } from "@/api/endpoints";

interface Props {
  value: ItemUomInput[];
  onChange: (rows: ItemUomInput[]) => void;
  /** Base UoM label shown as the "per" unit (e.g. "1 Box = N Tablet"). */
  baseUomLabel?: string;
  disabled?: boolean;
}

/**
 * Editor for an item's alternate units of measure / pack sizes (Feedback batch 1, Code 2).
 * Each row says how many BASE units the alternate unit contains, e.g. Box = 10 Tablets.
 */
export function AlternateUomEditor({ value, onChange, baseUomLabel, disabled }: Props) {
  const rows = value ?? [];

  const update = (idx: number, patch: Partial<ItemUomInput>) =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const remove = (idx: number) => onChange(rows.filter((_, i) => i !== idx));
  const add = () => onChange([...rows, { uomId: "", conversionFactor: 1 }]);

  return (
    <Box>
      <Stack spacing={1.5}>
        {rows.map((row, idx) => (
          <Stack key={idx} direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <UomPicker
                label="Pack unit"
                value={row.uomId ? ({ id: row.uomId, code: row.uomCode ?? "", name: row.uomName ?? "" } as UomDto) : null}
                initialId={row.uomId || undefined}
                onChange={(v) => update(idx, { uomId: v?.id ?? "", uomCode: v?.code, uomName: v?.name })}
                disabled={disabled}
              />
            </Box>
            <TextField
              type="number" size="small" label={`Units per pack${baseUomLabel ? ` (${baseUomLabel})` : ""}`}
              sx={{ width: 200 }}
              value={row.conversionFactor}
              onChange={(e) => update(idx, { conversionFactor: e.target.value === "" ? 0 : Number(e.target.value) })}
              disabled={disabled}
              inputProps={{ min: 0, step: "any" }}
            />
            <Tooltip title="Remove">
              <IconButton size="small" color="error" onClick={() => remove(idx)} disabled={disabled}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No alternate pack sizes. Add one to sell/purchase in boxes, strips, etc.
          </Typography>
        )}
      </Stack>
      <Button size="small" startIcon={<AddIcon />} onClick={add} disabled={disabled} sx={{ mt: 1 }}>
        Add pack size
      </Button>
    </Box>
  );
}
